/**
 * LinkEditorPage
 *
 * Full-page editor for creating and editing links.
 * Replaces the modal-based CreateLinkModal/EditLinkModal with a routable page.
 *
 * Routes:
 * - /links/new - Create new link
 * - /links/:shortCode/edit - Edit existing link
 *
 * Steps:
 * 1. Basics - Destination URL, title, vanity URL, project
 * 2. Options - Tags, expiration, max visits, password protection
 * 3. Publish - Summary and create/save button
 */

import { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Link2,
  Settings,
  CheckCircle2,
  Tag,
  Clock,
  Users,
  Plus,
  X,
  Layers,
  Lock,
  Eye,
  EyeOff,
  ShieldAlert,
  AlertTriangle,
  Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { FullPageEditorLayout } from '@/client/components/editors';
import { WizardStep, type WizardStepConfig } from '@/client/components/wizard';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Button } from '@/client/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { useFeatureGate, TierBadge } from '@/client/components/feature-gate';
import { useAuth } from '@/client/hooks/use-auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/client/components/ui/dialog';

// =============================================================================
// Types
// =============================================================================

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

interface LinkResponse {
  id: string;
  shortCode: string;
  shortUrl: string;
  destinationUrl: string;
  title?: string | null;
  tags?: string[];
  projectId?: string | null;
  validUntil?: string | null;
  maxVisits?: number | null;
  isActive?: boolean;
  hasPassword?: boolean;
  passwordHint?: string | null;
}

// =============================================================================
// Schema
// =============================================================================

const linkEditorSchema = z.object({
  url: z.string().url('Please enter a valid URL').max(2048),
  title: z.string().max(255).optional(),
  customCode: z
    .string()
    .max(50)
    .regex(/^[a-zA-Z0-9-_]*$/, 'Only letters, numbers, hyphens, and underscores')
    .optional(),
  projectId: z.string().optional(),
  tags: z.array(z.string()).optional(),
  validUntil: z.string().nullable().optional(),
  maxVisits: z.number().int().positive().max(1_000_000).nullable().optional(),
  isActive: z.boolean().optional(),
  password: z.string().min(4).max(128).nullable().optional(),
  passwordHint: z.string().max(100).nullable().optional(),
});

type LinkEditorInput = z.infer<typeof linkEditorSchema>;

// =============================================================================
// Wizard Steps Configuration
// =============================================================================

const getWizardSteps = (): WizardStepConfig[] => [
  { id: 'basics', label: 'Basics', icon: Link2 },
  { id: 'options', label: 'Options', icon: Settings, optional: true },
  { id: 'publish', label: 'Publish', icon: CheckCircle2 },
];

// =============================================================================
// Step Components
// =============================================================================

interface BasicsStepProps {
  projects: ProjectOption[];
  isEditMode: boolean;
  existingShortCode?: string;
}

function BasicsStep({ projects, isEditMode, existingShortCode }: BasicsStepProps) {
  const form = useFormContext<LinkEditorInput>();
  const { isPlusUser } = useAuth();

  // Feature gate for vanity URLs
  const {
    hasAccess: hasVanityAccess,
    openUpgradeModal,
    UpgradeModalElement,
  } = useFeatureGate('custom_short_codes', 'Create branded vanity URLs like arml.ink/your-brand');

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Destination URL - Primary Focus */}
      <div className="space-y-2">
        <Label htmlFor="url">
          Destination URL <span className="text-destructive">*</span>
        </Label>
        <Input
          type="url"
          id="url"
          autoComplete="url"
          inputMode="url"
          spellCheck={false}
          {...form.register('url')}
          className="font-mono text-sm"
          placeholder="https://example.com/your-long-url"
          autoFocus={!isEditMode}
        />
        {form.formState.errors.url && (
          <p className="text-sm text-destructive">{form.formState.errors.url.message}</p>
        )}
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">
          Title <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <Input
          type="text"
          id="title"
          autoComplete="off"
          spellCheck={true}
          {...form.register('title')}
          placeholder="My awesome link"
        />
        <p className="text-xs text-muted-foreground">
          A friendly name to help you identify this link
        </p>
      </div>

      {/* Vanity URL / Custom Short Code */}
      <div className="space-y-2">
        <Label htmlFor="customCode" className="flex items-center gap-2">
          Vanity URL
          <span className="text-muted-foreground text-xs">(optional)</span>
          {!hasVanityAccess && <TierBadge tier="plus" size="xs" />}
        </Label>

        {isEditMode ? (
          // In edit mode, show the existing short code (read-only)
          <div className="flex items-center">
            <span className="px-3 py-2.5 rounded-l-xl border border-r-0 bg-muted text-muted-foreground text-sm">
              arml.ink/
            </span>
            <div className="flex-1 px-3 py-2.5 rounded-r-xl border bg-muted/50 font-mono text-sm text-muted-foreground">
              {existingShortCode}
            </div>
          </div>
        ) : hasVanityAccess ? (
          // Plus user: show editable input
          <div className="flex items-center">
            <span className="px-3 py-2.5 rounded-l-xl border border-r-0 bg-muted text-muted-foreground text-sm">
              arml.ink/
            </span>
            <Input
              type="text"
              id="customCode"
              autoComplete="off"
              spellCheck={false}
              {...form.register('customCode')}
              className="rounded-l-none font-mono text-sm"
              placeholder="your-brand-name"
            />
          </div>
        ) : (
          // Free user: show gated input
          <button
            type="button"
            onClick={openUpgradeModal}
            className="w-full relative group text-left"
          >
            <div className="flex items-center opacity-50 pointer-events-none">
              <span className="px-3 py-2.5 rounded-l-xl border border-r-0 bg-muted text-muted-foreground text-sm">
                arml.ink/
              </span>
              <div className="flex-1 px-3 py-2.5 rounded-r-xl border bg-background font-mono text-sm text-muted-foreground">
                your-brand-name
              </div>
            </div>
            <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Crown className="size-2.5" />
              Plus
            </span>
          </button>
        )}

        {form.formState.errors.customCode && (
          <p className="text-sm text-destructive">{form.formState.errors.customCode.message}</p>
        )}
        {!isEditMode && (
          <p className="text-xs text-muted-foreground">Leave empty to auto-generate a short code</p>
        )}
        {UpgradeModalElement}
      </div>

      {/* Project Selection - Plus feature */}
      {isPlusUser && projects.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            Project
          </Label>
          <Select
            value={form.watch('projectId') || '__none__'}
            onValueChange={(value) =>
              form.setValue('projectId', value === '__none__' ? undefined : value)
            }
          >
            <SelectTrigger className="w-full max-w-xs rounded-xl">
              <SelectValue placeholder="No project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No project</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color || '#6366f1' }}
                    />
                    {project.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function OptionsStep({ isEditMode }: { isEditMode: boolean }) {
  const form = useFormContext<LinkEditorInput>();
  const [tagInput, setTagInput] = useState('');
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Feature gate for password protection
  const {
    hasAccess: hasPasswordAccess,
    requiredTier: passwordRequiredTier,
    openUpgradeModal: openPasswordUpgradeModal,
    UpgradeModalElement: PasswordUpgradeModal,
  } = useFeatureGate('link_password_protection', 'Protect your links with password authentication');

  const tags = form.watch('tags') || [];
  const isActive = form.watch('isActive') ?? true;

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      form.setValue('tags', [...tags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    form.setValue(
      'tags',
      tags.filter((t) => t !== tagToRemove)
    );
  };

  // Initialize password state from form values
  useEffect(() => {
    const password = form.getValues('password');
    if (password) {
      setPasswordEnabled(true);
    }
  }, [form]);

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Tags */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          Tags <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <div className="flex gap-2">
          <Input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Add a tag"
            className="flex-1"
          />
          <Button type="button" variant="outline" size="icon" onClick={addTag}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-sm"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-primary/70"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Expiration & Limits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="validUntil" className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Expires
          </Label>
          <Input type="datetime-local" id="validUntil" {...form.register('validUntil')} />
          <p className="text-xs text-muted-foreground">Leave empty for no expiration</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="maxVisits" className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Max Visits
          </Label>
          <Input
            type="number"
            id="maxVisits"
            inputMode="numeric"
            {...form.register('maxVisits', { valueAsNumber: true })}
            placeholder="Unlimited"
            min={1}
          />
          <p className="text-xs text-muted-foreground">Leave empty for unlimited</p>
        </div>
      </div>

      {/* Active Toggle (edit mode only) */}
      {isEditMode && (
        <div className="flex items-center justify-between py-3 px-4 rounded-xl border bg-muted/30">
          <div>
            <Label htmlFor="isActive" className="font-medium">
              Link Status
            </Label>
            <p className="text-xs text-muted-foreground">Inactive links will show a 404 error</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => form.setValue('isActive', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
          </label>
        </div>
      )}

      {/* Password Protection */}
      <div className="space-y-3 pt-4 border-t">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            Password Protection
            {!hasPasswordAccess && <TierBadge tier={passwordRequiredTier} size="xs" />}
          </Label>
          {hasPasswordAccess && (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={passwordEnabled}
                onChange={(e) => {
                  setPasswordEnabled(e.target.checked);
                  if (!e.target.checked) {
                    form.setValue('password', null);
                    form.setValue('passwordHint', null);
                  }
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
            </label>
          )}
        </div>

        {hasPasswordAccess ? (
          passwordEnabled ? (
            <div className="space-y-3 p-4 rounded-xl border bg-muted/20">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="new-password"
                    {...form.register('password')}
                    placeholder="Enter password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Minimum 4 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordHint" className="text-sm">
                  Password Hint <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  type="text"
                  id="passwordHint"
                  {...form.register('passwordHint')}
                  placeholder="e.g., The secret code from the meeting"
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground">
                  Shown on the password page to help visitors remember
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Require visitors to enter a password before accessing this link.
            </p>
          )
        ) : (
          <button
            type="button"
            onClick={openPasswordUpgradeModal}
            className="w-full p-4 rounded-xl border border-dashed bg-muted/30 text-center hover:bg-muted/50 hover:border-primary/30 transition-colors"
          >
            <p className="text-sm text-muted-foreground mb-1">Protect your links with a password</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
              <Crown className="h-3.5 w-3.5" />
              Upgrade to unlock
            </span>
          </button>
        )}

        {PasswordUpgradeModal}
      </div>
    </div>
  );
}

function PublishStep({ isEditMode, shortCode }: { isEditMode: boolean; shortCode?: string }) {
  const form = useFormContext<LinkEditorInput>();
  const url = form.watch('url');
  const title = form.watch('title');
  const customCode = form.watch('customCode');
  const tags = form.watch('tags') || [];
  const validUntil = form.watch('validUntil');
  const maxVisits = form.watch('maxVisits');
  const isActive = form.watch('isActive') ?? true;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-6 rounded-xl border bg-card">
        <h3 className="font-medium mb-4">Ready to {isEditMode ? 'Save' : 'Publish'}</h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Short URL</dt>
            <dd className="font-mono text-xs">
              arml.ink/{isEditMode ? shortCode : customCode || '(auto-generated)'}
            </dd>
          </div>
          {title && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Title</dt>
              <dd className="font-medium truncate max-w-[200px]">{title}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Destination</dt>
            <dd className="font-mono text-xs truncate max-w-[200px]" title={url}>
              {url}
            </dd>
          </div>
          {tags.length > 0 && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Tags</dt>
              <dd className="font-medium">
                {tags.length} tag{tags.length > 1 ? 's' : ''}
              </dd>
            </div>
          )}
          {validUntil && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Expires</dt>
              <dd className="font-medium">{new Date(validUntil).toLocaleDateString()}</dd>
            </div>
          )}
          {maxVisits && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Max Visits</dt>
              <dd className="font-medium">{maxVisits.toLocaleString()}</dd>
            </div>
          )}
          {isEditMode && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Status</dt>
              <dd
                className={cn('font-medium', isActive ? 'text-green-600' : 'text-muted-foreground')}
              >
                {isActive ? 'Active' : 'Inactive'}
              </dd>
            </div>
          )}
        </dl>
      </div>
      <p className="text-sm text-muted-foreground">
        {isEditMode
          ? 'Click "Save" to update your link.'
          : 'Click "Create" to make your link live. It will start tracking clicks immediately.'}
      </p>
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function LinkEditorPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPlusUser } = useAuth();

  const isEditMode = !!shortCode;

  // URL change warning state
  const [showUrlWarning, setShowUrlWarning] = useState(false);
  const [pendingData, setPendingData] = useState<LinkEditorInput | null>(null);
  const [unsafeUrlError, setUnsafeUrlError] = useState<string | null>(null);

  // Fetch projects
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
    enabled: isPlusUser,
  });

  const projects: ProjectOption[] = projectsData?.data || [];

  // Fetch existing link for edit mode
  const { data: existingLink, isLoading: isLoadingLink } = useQuery<LinkResponse>({
    queryKey: ['links', shortCode],
    queryFn: async () => {
      const response = await apiFetch(`/api/links/${shortCode}`);
      if (!response.ok) throw new Error('Failed to fetch link');
      return response.json();
    },
    enabled: isEditMode,
  });

  // Form setup
  const form = useForm<LinkEditorInput>({
    resolver: zodResolver(linkEditorSchema),
    defaultValues: {
      url: '',
      title: '',
      customCode: undefined,
      projectId: undefined,
      tags: [],
      validUntil: null,
      maxVisits: null,
      isActive: true,
      password: null,
      passwordHint: null,
    },
  });

  // Load existing link data
  useEffect(() => {
    if (existingLink && isEditMode) {
      form.reset({
        url: existingLink.destinationUrl,
        title: existingLink.title || '',
        projectId: existingLink.projectId || undefined,
        tags: existingLink.tags || [],
        validUntil: existingLink.validUntil ? existingLink.validUntil.slice(0, 16) : null,
        maxVisits: existingLink.maxVisits || null,
        isActive: existingLink.isActive !== false,
        password: null,
        passwordHint: existingLink.passwordHint || null,
      });
    }
  }, [existingLink, isEditMode, form]);

  const isDirty = form.formState.isDirty;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: LinkEditorInput) => {
      setUnsafeUrlError(null);
      const payload = {
        url: data.url,
        title: data.title || undefined,
        customCode: data.customCode?.trim() || undefined,
        projectId: data.projectId || undefined,
        tags: data.tags,
        validUntil: data.validUntil || undefined,
        maxVisits: Number.isNaN(data.maxVisits) ? undefined : data.maxVisits || undefined,
        password: data.password || undefined,
        passwordHint: data.passwordHint || undefined,
      };

      const response = await apiFetch('/api/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'UNSAFE_URL') {
          const unsafeError = new Error(error.message) as Error & { isUnsafe: boolean };
          unsafeError.isUnsafe = true;
          throw unsafeError;
        }
        throw new Error(error.message || 'Failed to create link');
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast.success('Link created successfully!', {
        description: `arml.ink/${data.shortCode}`,
        action: {
          label: 'Copy',
          onClick: () => {
            navigator.clipboard.writeText(data.shortUrl);
            toast.success('Link copied!');
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      navigate(`/links/${data.shortCode}`);
    },
    onError: (error: Error & { isUnsafe?: boolean }) => {
      if (error.isUnsafe) {
        setUnsafeUrlError(error.message);
      } else {
        toast.error(error.message);
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: LinkEditorInput) => {
      setUnsafeUrlError(null);
      const payload: Record<string, unknown> = {};

      // Only include changed fields
      if (data.title !== (existingLink?.title || '')) {
        payload.title = data.title || undefined;
      }
      if (data.url !== existingLink?.destinationUrl) {
        payload.destinationUrl = data.url;
      }
      if (JSON.stringify(data.tags) !== JSON.stringify(existingLink?.tags || [])) {
        payload.tags = data.tags;
      }
      const newProjectId = data.projectId === '' ? null : data.projectId;
      if (newProjectId !== (existingLink?.projectId || null)) {
        payload.projectId = newProjectId;
      }
      if (data.isActive !== (existingLink?.isActive !== false)) {
        payload.isActive = data.isActive;
      }
      const currentValidUntil = existingLink?.validUntil
        ? existingLink.validUntil.slice(0, 16)
        : null;
      if (data.validUntil !== currentValidUntil) {
        payload.validUntil = data.validUntil ? new Date(data.validUntil).toISOString() : null;
      }
      if (data.maxVisits !== (existingLink?.maxVisits || null)) {
        payload.maxVisits = data.maxVisits;
      }

      // Handle password
      if (data.password) {
        payload.password = data.password;
      }
      if (data.passwordHint !== (existingLink?.passwordHint || null)) {
        payload.passwordHint = data.passwordHint;
      }

      // Don't make request if nothing changed
      if (Object.keys(payload).length === 0) {
        return { noChanges: true };
      }

      const response = await apiFetch(`/api/links/${shortCode}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'UNSAFE_URL') {
          const unsafeError = new Error(error.message) as Error & { isUnsafe: boolean };
          unsafeError.isUnsafe = true;
          throw unsafeError;
        }
        throw new Error(error.message || error.error || 'Failed to update link');
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data?.noChanges) {
        toast.info('No changes to save');
        navigate('/links');
        return;
      }
      toast.success('Link updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['links', shortCode] });
      navigate('/links');
    },
    onError: (error: Error & { isUnsafe?: boolean }) => {
      if (error.isUnsafe) {
        setUnsafeUrlError(error.message);
      } else {
        toast.error(error.message);
      }
    },
  });

  // Handle complete
  const handleComplete = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error('Please fix the errors before saving');
      return;
    }

    const data = form.getValues();

    // Check if URL changed in edit mode
    if (isEditMode && data.url !== existingLink?.destinationUrl) {
      setPendingData(data);
      setShowUrlWarning(true);
      return;
    }

    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  }, [form, isEditMode, existingLink, updateMutation, createMutation]);

  // Confirm URL change
  const handleConfirmUrlChange = useCallback(() => {
    if (pendingData) {
      updateMutation.mutate(pendingData);
      setShowUrlWarning(false);
      setPendingData(null);
    }
  }, [pendingData, updateMutation]);

  // Cancel URL change
  const handleCancelUrlChange = useCallback(() => {
    if (existingLink) {
      form.setValue('url', existingLink.destinationUrl);
    }
    setShowUrlWarning(false);
    setPendingData(null);
  }, [form, existingLink]);

  // Step validation
  const validateStep = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      const step = wizardSteps[stepIndex];
      if (!step) return true;

      if (step.id === 'basics') {
        const result = await form.trigger(['url']);
        if (!result) {
          toast.error('Please enter a valid destination URL');
        }
        return result;
      }

      return true;
    },
    [form]
  );

  const wizardSteps = getWizardSteps();
  const isLoading = isEditMode && (isLoadingLink || isLoadingProjects);

  return (
    <FormProvider {...form}>
      <FullPageEditorLayout
        title={isEditMode ? 'Edit Link' : 'Create Link'}
        subtitle={
          isEditMode ? `Editing arml.ink/${shortCode}` : 'Shorten a URL and start tracking clicks'
        }
        steps={wizardSteps}
        initialStep={0}
        isEditMode={isEditMode}
        isLoading={isLoading}
        isDirty={isDirty}
        onComplete={handleComplete}
        completeButtonText={isEditMode ? 'Save' : 'Create'}
        accentColor="amber"
        backPath="/links"
        hasPreview={false}
        validateStep={validateStep}
      >
        <WizardStep stepId="basics">
          <BasicsStep projects={projects} isEditMode={isEditMode} existingShortCode={shortCode} />
          {/* Unsafe URL error display */}
          {unsafeUrlError && (
            <div className="mt-4 p-3 rounded-xl border border-orange-500/30 bg-orange-500/10 max-w-2xl">
              <div className="flex items-start gap-2">
                <ShieldAlert className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-600">
                    Unable to {isEditMode ? 'update' : 'create'} link
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This URL has been flagged as potentially unsafe.
                  </p>
                </div>
              </div>
            </div>
          )}
        </WizardStep>

        <WizardStep stepId="options">
          <OptionsStep isEditMode={isEditMode} />
        </WizardStep>

        <WizardStep stepId="publish">
          <PublishStep isEditMode={isEditMode} shortCode={shortCode} />
        </WizardStep>
      </FullPageEditorLayout>

      {/* URL Change Confirmation Dialog */}
      <Dialog open={showUrlWarning} onOpenChange={(open) => !open && handleCancelUrlChange()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm URL Change
            </DialogTitle>
            <DialogDescription>
              Changing the destination URL will affect all existing QR codes and shared links
              pointing to{' '}
              <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                arml.ink/{shortCode}
              </code>
              .
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <strong>Warning:</strong> Anyone who clicks this short link will immediately be
                redirected to the new destination, including:
              </p>
              <ul className="text-sm text-amber-700 dark:text-amber-400 mt-2 ml-4 list-disc space-y-1">
                <li>Links shared on social media</li>
                <li>QR codes already printed</li>
                <li>Links in emails or documents</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelUrlChange}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUrlChange}
              disabled={updateMutation.isPending}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {updateMutation.isPending ? 'Updating…' : 'Yes, Update URL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
}
